import { PortalPartnerStatsRO } from '@routellers/types/dist/partners-portal/portal-stats.ro';
import { BarChart } from '@mantine/charts';
import {
    Alert,
    Card,
    Center,
    Grid,
    Group,
    Loader,
    SimpleGrid,
    Stack,
    Text,
    ThemeIcon,
} from '@mantine/core';
import { IconCoins, IconInfoCircle, IconPercentage, IconShoppingBag } from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';

import { api } from '../api';
import { PageHeader } from '../components/page-header';
import { t } from '../dictionary';
import { useAuth } from '../providers/auth-context';
import { formatMoneyMap } from '../utils/money';

const StatCard = ({
    icon,
    label,
    value,
}: {
    icon: React.ReactNode;
    label: string;
    value: string;
}) => (
    <Card padding='lg'>
        <Group wrap='nowrap'>
            <ThemeIcon size={42} radius='md' variant='light' color='indigo'>
                {icon}
            </ThemeIcon>
            <div style={{ minWidth: 0 }}>
                <Text size='xs' c='dimmed' tt='uppercase' fw={600}>
                    {label}
                </Text>
                <Text fw={700} size='xl' truncate>
                    {value}
                </Text>
            </div>
        </Group>
    </Card>
);

export const DashboardPage = () => {
    const { partner } = useAuth();
    const [stats, setStats] = useState<PortalPartnerStatsRO | null>(null);

    useEffect(() => {
        api.stats()
            .then(({ data }) => setStats(data))
            .catch(() => setStats(null));
    }, []);

    if (!stats) {
        return (
            <Center h={300}>
                <Loader />
            </Center>
        );
    }

    const chartData = stats.monthly.map((m) => ({
        month: new Date(m.month).toLocaleString('en', { month: 'short' }),
        Sales: m.games,
    }));

    const hasSales =
        stats.totalGames > 0 || Object.values(stats.totalSales || {}).some((v) => v > 0);

    return (
        <>
            <PageHeader
                title={t.dashboard.welcome(partner.name)}
                subtitle={t.dashboard.authorSubtitle}
            />

            <Stack gap='lg'>
                {partner.commissionPercent === 0 && (
                    <Alert
                        variant='light'
                        color='indigo'
                        icon={<IconInfoCircle />}
                        title={t.dashboard.commissionPendingTitle}
                    >
                        {t.dashboard.commissionPendingText}
                    </Alert>
                )}

                <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }}>
                    <StatCard
                        icon={<IconPercentage size={22} />}
                        label={t.dashboard.commission}
                        value={`${stats.commissionPercent}%`}
                    />
                    <StatCard
                        icon={<IconShoppingBag size={22} />}
                        label={t.dashboard.gamesPlayed}
                        value={String(stats.totalGames)}
                    />
                    <StatCard
                        icon={<IconCoins size={22} />}
                        label={t.dashboard.totalEarned}
                        value={formatMoneyMap(stats.totalCommission)}
                    />
                </SimpleGrid>

                <Grid>
                    <Grid.Col span={12}>
                        <Card padding='lg'>
                            <Text fw={650} mb='md'>
                                {t.dashboard.monthlyChart}
                            </Text>
                            {hasSales ? (
                                <BarChart
                                    h={260}
                                    data={chartData}
                                    dataKey='month'
                                    series={[{ name: 'Sales', color: 'indigo.6' }]}
                                    withLegend={false}
                                    gridAxis='y'
                                    barProps={{ radius: 6 }}
                                />
                            ) : (
                                <Center h={200}>
                                    <Stack align='center' gap={4}>
                                        <Text fw={600}>{t.dashboard.noSalesYet}</Text>
                                        <Text size='sm' c='dimmed' ta='center' maw={420}>
                                            {t.dashboard.noSalesAuthorText}
                                        </Text>
                                    </Stack>
                                </Center>
                            )}
                        </Card>
                    </Grid.Col>
                </Grid>
            </Stack>
        </>
    );
};
